import React, { useState, useCallback, useEffect, Fragment, useMemo } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import CheckoutForm from './../../components/CheckoutForm';
import Userfront from '../../auth/userfrontCompat';
import './Subscription.css';
import api from './../../api/axios';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import InvoicingDetailsForm from './InvoicingDetailsForm';

// --- Stripe ---
const stripeApiKey = process.env.REACT_APP_STRIPE_API_KEY;
const stripePromise = stripeApiKey && typeof stripeApiKey === 'string' ? loadStripe(stripeApiKey) : null;

// ---- Helpers ----
const brandIconClass = (brandRaw) => {
  const brand = (brandRaw || '').toLowerCase();
  if (brand.includes('visa')) return 'fab fa-cc-visa';
  if (brand.includes('master')) return 'fab fa-cc-mastercard';
  if (brand.includes('amex')) return 'fab fa-cc-amex';
  if (brand.includes('discover')) return 'fab fa-cc-discover';
  if (brand.includes('diners')) return 'fab fa-cc-diners-club';
  if (brand.includes('jcb')) return 'fab fa-cc-jcb';
  return 'far fa-credit-card';
};
const methodIconClass = (method) => {
  if (method.type === 'sepa_debit') return 'fas fa-university';
  if (method.type === 'card') return brandIconClass(method.card?.brand);
  return 'far fa-credit-card';
};
const methodAccent = (method) => {
  if (method.type === 'sepa_debit') return 'bg-indigo-50 text-indigo-700 ring-indigo-100';
  if (method.type === 'card') return 'bg-blue-50 text-blue-700 ring-blue-100';
  return 'bg-gray-50 text-gray-700 ring-gray-100';
};
const formatExpiry = (m, y) => (m && y ? `${String(m).padStart(2, '0')}/${y}` : '—');

const currencyCodeToSymbol = (currency) => {
  const map = { USD: '$', EUR: '€', GBP: '£', JPY: '¥', AUD: 'A$', CAD: 'C$', CHF: 'CHF', CNY: '¥', SEK: 'kr', NZD: 'NZ$' };
  return map[currency?.toUpperCase?.() || currency] || currency;
};
const capitalizeFirstLetter = (s) => (!s ? '' : s.charAt(0).toUpperCase() + s.slice(1).toLowerCase());
const isSameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const formatDate = (date, locale = 'en') =>
  date
    ? date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
    : '—';

const Chip = ({ children, tone = 'gray', ...rest }) => {
  const tones = {
    green: 'bg-green-50 text-green-700 border-green-200',
    gray: 'bg-gray-100 text-gray-700 border-gray-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  return (
    <span {...rest} className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${tones[tone]}`}>
      {children}
    </span>
  );
};

const Skeleton = ({ className }) => <div className={`animate-pulse bg-gray-200/70 rounded ${className}`} />;

// ====================== COMPONENT ======================
const SubscriptionComponent = () => {
  const [loading, setLoading] = useState(true);

  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [invoiceData, setInvoiceData] = useState([]);

  const [message, setMessage] = useState('');
  const [checkoutSyncing, setCheckoutSyncing] = useState(false);
  const [showBasicPlans, setShowBasicPlans] = useState(true);
  const [activeTab, setActiveTab] = useState('activeSubscriptions');

  const [invoicingDetails, setInvoicingDetails] = useState({
    companyName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postalCode: '',
    country: '',
    state: '',
    taxId: '',
    taxIdType: 'eu_vat',
  });
  const [organizationId, setOrganizationId] = useState('');
  const [error, setError] = useState('');

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [activePaymentMethodId, setActivePaymentMethodId] = useState('');

  // Seats / users
  const [orgUsers, setOrgUsers] = useState([]);
  const [totalSeats, setTotalSeats] = useState(0);

  // Upcoming & last invoice
  const [upcomingInvoice, setUpcomingInvoice] = useState(null);
  const [lastInvoice, setLastInvoice] = useState(null);

  const location = useLocation();
  const adminEmail = Userfront.user.email;

  const { t, i18n } = useTranslation();
  const [userLanguage, setUserLanguage] = useState('en');

  // --- language
  const fetchUserLanguage = useCallback(async () => {
    try {
      const response = await api.get(`/user/user`);
      const lang = response.data.language || response.data.user?.language;

      if (lang) {
        setUserLanguage(lang);
        i18n.changeLanguage(lang);
      }
    } catch (err) {
      console.error("Error fetching user settings:", err);
    }
  }, [i18n]); // i18n is referenced inside, so include it

  useEffect(() => {
    fetchUserLanguage();
  }, [fetchUserLanguage]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const sessionId = query.get('session_id');
    if (!sessionId) {
      if (query.get('cancelled')) setMessage('Subscription was cancelled.');
      return undefined;
    }

    let cancelled = false;
    const syncCheckout = async () => {
      setCheckoutSyncing(true);
      setMessage('Payment completed. Activating your subscription…');
      for (let attempt = 0; attempt < 15 && !cancelled; attempt += 1) {
        try {
          const access = await api.get('/subscription/access-status');
          if (access.data?.hasPaidAccess) {
            setMessage('Subscription activated. Opening your dashboard…');
            window.setTimeout(() => window.location.replace('/?subscription=success'), 350);
            return;
          }
        } catch (syncError) {
          console.warn('Waiting for Stripe subscription activation:', syncError);
        }
        await new Promise((resolve) => window.setTimeout(resolve, 1000));
      }
      if (!cancelled) {
        setCheckoutSyncing(false);
        setMessage('Payment was received, but activation is taking longer than expected. Refresh this page in a moment.');
      }
    };
    syncCheckout();
    return () => { cancelled = true; };
  }, [location.search]);

  // --- load core user/org context
  useEffect(() => {
    const fetchContext = async () => {
      try {
        const userResponse = await api.get(`/user/user`);
        const currentUser = userResponse.data.user;
        if (currentUser?.organizationId) setOrganizationId(currentUser.organizationId);
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    fetchContext();
  }, [adminEmail]);

  // --- main loaders
  useEffect(() => {
    const fetchActiveSubscriptions = async () => {
      try {
        const response = await api.get(`/subscription/subscriptions?email=${adminEmail}`);
        const subscriptions = response.data.subscriptions || [];
        setActiveSubscriptions(subscriptions);

        const hasTeam = subscriptions.some((s) => s.planName?.includes('Team'));
        setShowBasicPlans(!hasTeam);
        setActiveTab(subscriptions.length > 0 ? 'activeSubscriptions' : 'choosePlan');
      } catch (error) {
        console.error('Error fetching active subscriptions:', error);
      }
    };

    const fetchInvoices = async () => {
      try {
        const userResponse = await api.get(`/user/user`);
        const currentUser = userResponse.data.user;
        if (!currentUser?.organizationId) return;
        const invoicesResponse = await api.get(`/subscription/invoices?organizationId=${currentUser.organizationId}`);
        const invoices = (invoicesResponse.data.invoices || []).map((invoice) => ({
          ...invoice,
          _date: new Date(invoice.date), // keep Date object for sorting
          date: new Date(invoice.date).toLocaleDateString(userLanguage || 'nl-NL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }),
        }));
        setInvoiceData(invoices);

        // last invoice
        if (invoices.length > 0) {
          const sorted = [...invoices].sort((a, b) => b._date - a._date);
          setLastInvoice(sorted[0]);
        } else {
          setLastInvoice(null);
        }
      } catch (error) {
        console.error('Error fetching invoices:', error);
      }
    };

    const fetchInvoicingDetails = async () => {
      try {
        if (!organizationId) return;
        const response = await api.get(`/user/organizations/${organizationId}`);
        setInvoicingDetails(response.data.invoicingDetails || {});
      } catch (error) {
        console.error('Error fetching invoicing details:', error);
        setError('Error fetching invoicing details: ' + (error.response?.data?.error || 'Unknown error'));
      }
    };

    const fetchPaymentMethods = async () => {
      try {
        const userResponse = await api.get(`/user/user`);
        const currentUser = userResponse.data.user;
        if (!currentUser?.stripeCustomerId) return;

        const response = await api.get(`/subscription/payment-methods?stripeCustomerId=${currentUser.stripeCustomerId}`);
        setPaymentMethods(response.data.paymentMethods || []);

        const activeMethodResponse = await api.get(`/subscription/active-payment-method?customerId=${currentUser.stripeCustomerId}`);
        const activeMethod = activeMethodResponse.data;
        setActivePaymentMethodId(activeMethod?.id || '');

        // upcoming invoice
        try {
          const up = await api.get(`/subscription/upcoming-invoice?customerId=${currentUser.stripeCustomerId}`);
          setUpcomingInvoice(up.data || null);
        } catch (e) {
          // not fatal if missing
          setUpcomingInvoice(null);
        }
      } catch (error) {
        console.error('Error fetching payment methods:', error);
      }
    };

    const fetchOrganizationUsers = async () => {
      try {
        const resp = await api.get(`/user/organization/users`);
        const users = resp.data.users || [];
        setOrgUsers(users);
      } catch (err) {
        console.error('Error fetching organization users:', err);
        setOrgUsers([]);
      }
    };

    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchActiveSubscriptions(),
        fetchInvoices(),
        fetchPaymentMethods(),
        fetchOrganizationUsers(),
      ]);
      await fetchInvoicingDetails();
      setLoading(false);
    };

    fetchAll();
  }, [adminEmail, organizationId, userLanguage]);

  // ---------- Actions ----------
  const handleSubscriptionSuccess = () => setMessage('Subscription successful!');

  const handleOpenCustomerPortalSubscription = async (subscriptionId) => {
    try {
      const userResponse = await api.get(`/user/user`);
      const user = userResponse.data.user;
      if (!user?.stripeCustomerId) return;

      const response = await api.post('/subscription/create-customer-portal-session-subscription', {
        stripeCustomerId: user.stripeCustomerId,
        subscriptionId,
      });
      if (response.data.url) window.location.href = response.data.url;
    } catch (error) {
      console.error('Error opening Stripe Customer Portal:', error);
    }
  };

  const handleOpenCustomerPortal = async () => {
    try {
      const userResponse = await api.get(`/user/user`);
      const user = userResponse.data.user;
      if (!user?.stripeCustomerId) return;

      const response = await api.post('/subscription/create-customer-portal-session', {
        stripeCustomerId: user.stripeCustomerId,
      });
      if (response.data.url) window.location.href = response.data.url;
    } catch (error) {
      console.error('Error opening Stripe Customer Portal:', error);
    }
  };

  // Direct cancel flow
  const handleCancelSubscription = async (subscriptionId) => {
    try {
      const userResponse = await api.get(`/user/user`);
      const user = userResponse.data.user;
      if (!user?.stripeCustomerId) return;

      const response = await api.post('/subscription/create-customer-portal-session-cancel', {
        stripeCustomerId: user.stripeCustomerId,
        subscriptionId,
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error opening cancel flow:', error);
    }
  };

  const handleSaveInvoicingDetails = async () => {
    try {
      if (invoicingDetails.taxId && !invoicingDetails.taxIdType) {
        setError('Tax ID Type is required when Tax ID is provided');
        return;
      }
      await api.put('/subscription/update-invoicing-details', {
        email: adminEmail,
        organizationId,
        invoicingDetails,
      });
    } catch (error) {
      console.error('Error saving invoicing details:', error.response?.data || error);
      setError('Error saving invoicing details: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleChangeActivePaymentMethod = async (paymentMethodId) => {
    try {
      const userResponse = await api.get(`/user/user`);
      const user = userResponse.data.user;
      const stripeCustomerId = user?.stripeCustomerId;
      if (!stripeCustomerId) return;

      const response = await api.post('/subscription/update-active-payment-method', { stripeCustomerId, paymentMethodId });
      if (response.data.success) {
        setActivePaymentMethodId(paymentMethodId);
        setMessage('Payment method updated successfully!');
      }
    } catch (error) {
      console.error('Error changing active payment method:', error);
      setError('Error changing active payment method: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleDeletePaymentMethod = async (paymentMethodId) => {
    try {
      const userResponse = await api.get(`/user/user`);
      const user = userResponse.data.user;
      const stripeCustomerId = user?.stripeCustomerId;
      if (!stripeCustomerId) return;

      const response = await api.post('/subscription/delete-payment-method', { stripeCustomerId, paymentMethodId });
      if (response.data.success) {
        setPaymentMethods((prev) => prev.filter((m) => m.id !== paymentMethodId));
        setMessage('Payment method deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting payment method:', error);
      setError('Error deleting payment method: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  // --------- derived UI data ----------
  const main = activeSubscriptions[0];

  // quantities / seats
  const totalSeatsComputed = useMemo(
    () => activeSubscriptions.reduce((acc, s) => acc + (Number(s.quantity) || 0), 0),
    [activeSubscriptions]
  );
  useEffect(() => setTotalSeats(totalSeatsComputed), [totalSeatsComputed]);

  const seatsUsed = useMemo(
    () => orgUsers.filter((u) => u.subscriptionStatus === 'Active').length,
    [orgUsers]
  );

  // default payment method object
  const defaultPayment = useMemo(
    () => paymentMethods.find((m) => m.id === activePaymentMethodId) || paymentMethods[0],
    [paymentMethods, activePaymentMethodId]
  );

  // period boundaries (opt-in; only show if both exist)
  const periodStart = useMemo(() => {
    // server should add these seconds on subscription object; if absent -> null
    return main?.currentPeriodStart ? new Date(main.currentPeriodStart * 1000) : null;
  }, [main]);
  const periodEnd = useMemo(() => {
    return main?.currentPeriodEnd ? new Date(main.currentPeriodEnd * 1000) : null;
  }, [main]);

  // next charge date & rules
  const nextPaymentDate = useMemo(() => {
    if (upcomingInvoice?.nextPayment) return new Date(upcomingInvoice.nextPayment * 1000);
    return periodEnd || null;
  }, [upcomingInvoice, periodEnd]);

  const showBothDates = useMemo(() => {
    const differentDay = nextPaymentDate && periodEnd && !isSameDay(nextPaymentDate, periodEnd);
    const hasEarlyCharge =
      (upcomingInvoice?.amountDue ?? 0) > 0 &&
      nextPaymentDate &&
      periodStart &&
      nextPaymentDate < periodEnd;
    return Boolean(differentDay || hasEarlyCharge);
  }, [nextPaymentDate, periodEnd, upcomingInvoice, periodStart]);

  const nextBillLabel = useMemo(() => {
    const ends = main?.cancelAtPeriodEnd;
    return ends ? t('subscription.endsOn') : t('subscription.renewsOn');
  }, [main, t]);

  const statusTone = (status) => {
    if (status === 'active') return 'green';
    if (status === 'past_due') return 'amber';
    if (['canceled', 'unpaid', 'incomplete_expired'].includes(status)) return 'red';
    return 'gray';
  };

  const planTitle = useMemo(() => main?.planName || t('subscription.subscription'), [main, t]);
  const planSubline = useMemo(() => {
    const isTeam = (main?.planName || '').includes('Team');
    const isYr = (main?.planName || '').toLowerCase().includes('yearly');
    return `${isTeam ? t('subscription.teamPlan') : t('subscription.individualPlan')} — ${isYr ? t('subscription.yearly') : t('subscription.monthly')}`;
  }, [main, t]);

  const lastInvoicePretty = useMemo(() => {
    if (!lastInvoice) return null;
    const prettyAmount = `${currencyCodeToSymbol(lastInvoice.currency)}${Number(lastInvoice.amount).toFixed(2)}`;
    const prettyDate = formatDate(lastInvoice._date, userLanguage);
    return { prettyAmount, prettyDate };
  }, [lastInvoice, userLanguage]);

  return (
    <div className="border-t border-gray-200">
      <div className="min-h-screen bg-gray-50 transition-all px-3 sm:px-4 xl:px-8 pt-4 pb-10">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-semibold text-gray-900">{t('subscription.billing')}</h1>
            <a href="/help" className="text-sm text-blue-600 hover:underline">
              {t('common.helpCenter')}
            </a>
          </div>
          {message && (
            <div className={`mb-4 rounded-xl border px-4 py-3 text-sm ${checkoutSyncing ? 'border-blue-200 bg-blue-50 text-blue-800' : 'border-gray-200 bg-white text-gray-700'}`}>
              {checkoutSyncing && <span className="mr-2 inline-block animate-spin">◌</span>}
              {message}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-6 border-b">
            {activeSubscriptions.length === 0 && (
              <button
                className={`pb-3 -mb-px text-sm font-medium border-b-2 ${
                  activeTab === 'choosePlan' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('choosePlan')}
              >
                {t('subscription.choosePlan')}
              </button>
            )}
            <button
              className={`pb-3 -mb-px text-sm font-medium border-b-2 ${
                activeTab === 'activeSubscriptions' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('activeSubscriptions')}
            >
              {t('subscription.subscription')}
            </button>
            <button
              className={`pb-3 -mb-px text-sm font-medium border-b-2 ${
                activeTab === 'invoices' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('invoices')}
            >
              {t('subscription.invoices')}
            </button>
            <button
              className={`pb-3 -mb-px text-sm font-medium border-b-2 ${
                activeTab === 'settings' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('settings')}
            >
              {t('subscription.settings')}
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white border rounded-2xl p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="w-2/3">
                    <Skeleton className="h-5 w-1/2" />
                    <Skeleton className="h-4 w-2/3 mt-2" />
                  </div>
                  <Skeleton className="h-6 w-16 rounded-full" />
                </div>
                <Skeleton className="h-2 w-full mt-6 rounded" />
                <Skeleton className="h-8 w-40 mt-6 rounded" />
              </div>
              <div className="bg-white border rounded-2xl p-5 shadow-sm">
                <Skeleton className="h-5 w-1/3" />
                <Skeleton className="h-4 w-2/3 mt-4" />
                <Skeleton className="h-8 w-48 mt-6" />
              </div>
            </div>
          ) : (
            <Fragment>
              {activeTab === 'choosePlan' && activeSubscriptions.length === 0 && (
                <div className="mt-6">
                  <Elements stripe={stripePromise}>
                    <div className="bg-white border rounded-2xl p-5 shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('subscription.choosePlan')}</h3>
                      <p className="text-sm text-gray-500 mb-4">{t('subscription.choosePlanSubtitle')}</p>
                      <CheckoutForm
                        email={adminEmail}
                        onSuccess={handleSubscriptionSuccess}
                        showBasicPlans={showBasicPlans}
                        activeSubscriptions={activeSubscriptions}
                      />
                    </div>
                  </Elements>
                </div>
              )}

              {activeTab === 'activeSubscriptions' && (
                <div className="mt-6">
                  {activeSubscriptions.length > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Plan Summary */}
                      <div className="bg-white border rounded-2xl p-5 shadow-sm">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <h2 className="text-lg font-semibold text-gray-900 truncate">
                              {planTitle}
                            </h2>
                            <p className="text-sm text-gray-500">{planSubline}</p>
                          </div>
                          <Chip tone={statusTone(main?.status)}>
                            {t(`subscriptionStatuses.${main?.status}`)}
                          </Chip>
                        </div>

                        {/* Key tiles */}
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {/* Facturatieperiode (alleen tonen als beide datums bestaan) */}
                          {(periodStart && periodEnd) ? (
                            <div className="rounded-lg border p-3">
                              <div className="text-xs text-gray-500">{t('subscription.billingPeriod')}</div>
                              <div className="mt-1 text-sm text-gray-900">
                                {formatDate(periodStart, userLanguage)} {' – '} {formatDate(periodEnd, userLanguage)}
                              </div>
                            </div>
                          ) : (
                            <div className="rounded-lg border p-3 opacity-60">
                              <div className="text-xs text-gray-500">{t('subscription.billingPeriod')}</div>
                              <div className="mt-1 text-sm text-gray-900">—</div>
                            </div>
                          )}

                          {/* Next bill / renews */}
                          {showBothDates ? (
                            <div className="rounded-lg border p-3">
                              <div className="text-xs text-gray-500">{t('subscription.nextBill')}</div>
                              <div className="mt-1 text-sm text-gray-900">
                                {nextPaymentDate ? formatDate(nextPaymentDate, userLanguage) : '—'}
                              </div>
                              {!!upcomingInvoice?.amountDue && (
                                <div className="text-xs text-gray-500">
                                  {t('subscription.amountDue')}: {currencyCodeToSymbol(upcomingInvoice.currency || 'EUR')}
                                  {(Number(upcomingInvoice.amountDue) / 100).toFixed(2)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="rounded-lg border p-3">
                              <div className="text-xs text-gray-500">{nextBillLabel}</div>
                              <div className="mt-1 text-sm text-gray-900">
                                {periodEnd ? formatDate(periodEnd, userLanguage) : (nextPaymentDate ? formatDate(nextPaymentDate, userLanguage) : '—')}
                              </div>
                              {!!upcomingInvoice?.amountDue && (
                                <div className="text-xs text-gray-500">
                                  {t('subscription.amountDue')}: {currencyCodeToSymbol(upcomingInvoice.currency || 'EUR')}
                                  {(Number(upcomingInvoice.amountDue) / 100).toFixed(2)}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Seats */}
                          <div className="rounded-lg border p-3">
                            <div className="text-xs text-gray-500">{t('subscription.seatsUsed')}</div>
                            <div className="mt-1 text-lg font-semibold text-gray-900">
                              {seatsUsed} / {totalSeats || 0}
                            </div>
                            <div className="text-xs text-gray-500">
                              {t('subscription.seatsUsedHint')}
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="mt-5 flex flex-wrap gap-2">
                          <button
                            onClick={() => handleOpenCustomerPortalSubscription(main.id)}
                            className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
                          >
                            {t('subscription.updateSubscription')}
                          </button>
                          <button
                            onClick={() => handleCancelSubscription(main.id)}
                            className="px-4 py-2 text-sm rounded-md bg-white border border-red-600 text-red-700 hover:bg-red-50"
                          >
                            {t('subscription.cancel')}
                          </button>
                        </div>
                      </div>

                      {/* Billing Meta: payment info + last invoice */}
                      <div className="bg-white border rounded-2xl p-5 shadow-sm space-y-4">
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">{t('subscription.paymentInfo')}</h3>
                          <div className="mt-3 text-sm">
                            {defaultPayment ? (
                              (() => {
                                const iconCls = methodIconClass(defaultPayment);
                                const accent = methodAccent(defaultPayment);
                                return (
                                  <div className="flex items-center gap-3">
                                    <div className={`h-9 w-9 rounded-full ring-1 flex items-center justify-center ${accent}`}>
                                      <i className={`${iconCls}`} />
                                    </div>
                                    <div className="text-gray-900">
                                      {defaultPayment.type === 'card'
                                        ? `${capitalizeFirstLetter(defaultPayment.card?.brand || 'Card')} •••• ${defaultPayment.card?.last4} (${formatExpiry(
                                            defaultPayment.card?.exp_month,
                                            defaultPayment.card?.exp_year
                                          )})`
                                        : defaultPayment.type === 'sepa_debit'
                                        ? `SEPA •••• ${defaultPayment.sepa_debit?.last4}`
                                        : t('subscription.unsupportedPaymentMethod')}
                                    </div>
                                  </div>
                                );
                              })()
                            ) : (
                              <div className="text-gray-500">{t('subscription.noPaymentMethods')}</div>
                            )}
                          </div>

                          <div className="mt-4 flex flex-wrap gap-2">
                            <button
                              onClick={() => setActiveTab('settings')}
                              className="px-4 py-2 text-sm rounded-md bg-white border text-gray-700 hover:bg-gray-50"
                            >
                              {t('subscription.changePaymentMethod')}
                            </button>
                            <button
                              onClick={() => setActiveTab('settings')}
                              className="px-4 py-2 text-sm rounded-md bg-white border text-gray-700 hover:bg-gray-50"
                            >
                              {t('subscription.editBillingDetails')}
                            </button>
                          </div>
                        </div>

                        <div className="border-t pt-4">
                          <h3 className="text-sm font-medium text-gray-900">{t('subscription.lastInvoice')}</h3>
                          {lastInvoicePretty ? (
                            <div className="mt-2 text-sm">
                              <div className="text-gray-900">{lastInvoicePretty.prettyAmount}</div>
                              <div className="text-gray-500">
                                {t('subscription.invoiceDate')}: {lastInvoicePretty.prettyDate}
                              </div>
                              <div className="mt-1">
                                <a
                                  href={lastInvoice?.invoicePdf}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                                >
                                  {t('subscription.download')} PDF <i className="fas fa-download" />
                                </a>
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">{t('subscription.noInvoicesFound')}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white border rounded-2xl p-8 shadow-sm mt-6 text-center text-sm text-gray-600">
                      {t('subscription.noActiveSubscriptions')}
                    </div>
                  )}

                  {/* overige abonnementen (optioneel) */}
                  {activeSubscriptions.length > 1 && (
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {activeSubscriptions.slice(1).map((subscription) => (
                        <div key={subscription.id} className="bg-white border rounded-lg p-4 shadow-sm">
                          <div className="flex justify-between items-start mb-2">
                            <div className="min-w-0">
                              <div className="font-semibold truncate">{subscription.planName}</div>
                              <div className="text-sm text-gray-500">
                                {(subscription.planName?.includes('Team') ? t('subscription.teamPlan') : t('subscription.individualPlan'))}
                                {' — '}
                                {(subscription.planName || '').toLowerCase().includes('yearly') ? t('subscription.yearly') : t('subscription.monthly')}
                              </div>
                            </div>
                            <Chip tone={statusTone(subscription.status)}>
                              {t(`subscriptionStatuses.${subscription.status}`)}
                            </Chip>
                          </div>

                          <div className="mt-3 flex gap-2">
                            <button
                              onClick={() => handleOpenCustomerPortalSubscription(subscription.id)}
                              className="border border-blue-600 text-blue-600 px-4 py-2 text-sm rounded-md"
                            >
                              {t('subscription.updateSubscription')}
                            </button>
                            <button
                              onClick={() => handleCancelSubscription(subscription.id)}
                              className="border border-red-600 text-red-700 px-4 py-2 text-sm rounded-md hover:bg-red-50"
                            >
                              {t('subscription.cancel')}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Invoices */}
              {activeTab === 'invoices' && (
                <div className="mt-6">
                  <div className="bg-white border rounded-2xl p-5 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('subscription.invoices')}</h3>
                    {invoiceData.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="bg-gray-100 text-left">
                              <th className="p-2">{t('subscription.invoiceId')}</th>
                              <th className="p-2">{t('subscription.total')}</th>
                              <th className="p-2">{t('subscription.status')}</th>
                              <th className="p-2">{t('subscription.invoiceDate')}</th>
                              <th className="p-2">{t('subscription.download')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoiceData.map((invoice) => (
                              <tr key={invoice.id} className="border-t">
                                <td className="p-2">{invoice.number}</td>
                                <td className="p-2">
                                  {currencyCodeToSymbol(invoice.currency)}
                                  {Number(invoice.amount).toFixed(2)}
                                </td>
                                <td className="p-2">
                                  <Chip tone={invoice.status === 'paid' ? 'green' : 'gray'}>
                                    {t(`invoiceStatuses.${invoice.status}`)}
                                  </Chip>
                                </td>
                                <td className="p-2">{invoice.date}</td>
                                <td className="p-2">
                                  <a href={invoice.invoicePdf} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                                    {t('subscription.download')} <i className="fas fa-download" />
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600">{t('subscription.noInvoicesFound')}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Settings */}
              {activeTab === 'settings' && (
                <div className="mt-6 space-y-6">
                  <div className="bg-white border rounded-2xl p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">{t('subscription.paymentMethods')}</h3>
                      <button onClick={handleOpenCustomerPortal} className="px-3 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700">
                        {t('subscription.addPaymentMethod')}
                      </button>
                    </div>

                    <ul className="mt-4 space-y-3">
                      {paymentMethods.length === 0 && (
                        <li className="border rounded-md p-6 text-sm text-gray-600 text-center">
                          {t('subscription.noPaymentMethods')}
                        </li>
                      )}

                      {paymentMethods.map((method) => {
                        const isDefault = method.id === activePaymentMethodId;
                        const iconCls = methodIconClass(method);
                        const accent = methodAccent(method);

                        let primary = '';
                        let secondary = '';
                        if (method.type === 'card') {
                          primary = `${capitalizeFirstLetter(method.card?.brand || 'Card')} ${t('subscription.endingIn')} ${method.card?.last4}`;
                          secondary = `${t('subscription.expires')} ${formatExpiry(method.card?.exp_month, method.card?.exp_year)}`;
                        } else if (method.type === 'sepa_debit') {
                          primary = `SEPA ${t('subscription.endingIn')} ${method.sepa_debit?.last4}`;
                          secondary = `${t('subscription.bank')}: ${method.sepa_debit?.bank_code || '—'}`;
                        } else {
                          primary = t('subscription.unsupportedPaymentMethod');
                          secondary = '';
                        }

                        return (
                          <li key={method.id} className="border rounded-lg p-4 shadow-sm hover:shadow transition-shadow">
                            <div className="flex items-start sm:items-center gap-4 sm:gap-6">
                              <div className={`shrink-0 h-10 w-10 rounded-full ring-1 flex items-center justify-center ${accent}`} aria-hidden="true">
                                <i className={`${iconCls} text-lg`} />
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                  <div className="min-w-0">
                                    <div className="font-medium text-gray-900 truncate">{primary}</div>
                                    {secondary && <div className="text-xs text-gray-500">{secondary}</div>}
                                  </div>

                                  <div className="flex items-center gap-2 sm:gap-3">
                                    {isDefault ? (
                                      <Chip tone="green">
                                        <i className="fas fa-check-circle mr-1" aria-hidden="true" />
                                        {t('subscription.active')}
                                      </Chip>
                                    ) : (
                                      <Fragment>
                                        <button
                                          onClick={() => handleChangeActivePaymentMethod(method.id)}
                                          className="inline-flex items-center gap-1 text-xs sm:text-sm px-3 py-1.5 rounded-md border border-blue-600 text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                          aria-label={t('subscription.setActive')}
                                        >
                                          <i className="fas fa-star" aria-hidden="true" />
                                          {t('subscription.setActive')}
                                        </button>
                                        <button
                                          onClick={() => {
                                            if (window.confirm(t('subscription.confirmDeletePaymentMethod'))) {
                                              handleDeletePaymentMethod(method.id);
                                            }
                                          }}
                                          className="inline-flex items-center gap-1 text-xs sm:text-sm px-3 py-1.5 rounded-md border border-red-600 text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200"
                                          aria-label={t('subscription.delete')}
                                        >
                                          <i className="fas fa-trash-alt" aria-hidden="true" />
                                          {t('subscription.delete')}
                                        </button>
                                      </Fragment>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <div className="bg-white border rounded-2xl p-5 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('subscription.billingDetails')}</h3>
                    <InvoicingDetailsForm
                      invoicingDetails={invoicingDetails}
                      setInvoicingDetails={setInvoicingDetails}
                      handleSaveInvoicingDetails={handleSaveInvoicingDetails}
                      error={error}
                    />
                  </div>
                </div>
              )}
            </Fragment>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionComponent;
